/**
 * Stap 4: verzamel je argumenten, voor en tegen, met de hulp van Socrates.
 *
 * De argumenten staan in het scherm (LeerlingFlow), want de stap Afronden
 * laat ze nog eens zien. Opslaan kan pas als de tijd om is.
 */
import React from 'react';
import { ChevronLeft, Plus, X } from 'lucide-react';
import { kleur } from '../../../theme/tokens';
import { basis } from '../stijlen';
import { GroepBadge, Tijdklok } from '../onderdelen/GroepEnKlok';
import { HulpVanSocrates } from '../onderdelen/HulpVanSocrates';
import { GesprekMeelezen } from '../onderdelen/GesprekMeelezen';

export function Argumenten({ api, session, discussion, groepje, argumenten, onWijzig, hulp, berichten, klok, onTerug, onOpgeslagen }) {
  const kanten = { voor: argumenten.voor, tegen: argumenten.tegen };

  const zet = (kant, lijst) => onWijzig({ ...argumenten, [kant]: lijst });
  const voegToe = (kant) => zet(kant, [...kanten[kant], '']);
  const wijzig = (kant, index, waarde) => zet(kant, kanten[kant].map((a, i) => (i === index ? waarde : a)));
  const haalWeg = (kant, index) => zet(kant, kanten[kant].filter((_, i) => i !== index));

  const slaOp = async () => {
    const voor = argumenten.voor.filter(a => a.trim());
    const tegen = argumenten.tegen.filter(a => a.trim());
    if (voor.length === 0 && tegen.length === 0) { alert('Voeg minimaal één argument toe'); return; }
    try {
      const data = await api.slaArgumentenOp(discussion.discussion_id, voor, tegen);
      if (data.success) onOpgeslagen();
    } catch (err) { console.error('Fout:', err); }
  };

  const tijdLooptNog = klok.seconden !== null && klok.seconden > 0;

  const kolom = (kant, titel) => (
    <div style={styles.argumentenColumn}>
      <h3 style={styles.argumentenTitle}>{titel}</h3>
      {kanten[kant].map((arg, idx) => (
        <div key={idx} style={styles.argumentRow}>
          <input type="text" value={arg} onChange={(e) => wijzig(kant, idx, e.target.value)} placeholder="Typ een argument..." style={styles.argumentInput} />
          <button onClick={() => hulp.vraag('kritiek', { tekst: arg.trim(), kant })}
            disabled={!arg.trim() || hulp.bezig === 'kritiek'}
            title="Laat Socrates vragen stellen bij dit argument"
            style={arg.trim() ? styles.kritiekBtn : styles.kritiekBtnUit}>?</button>
          {kanten[kant].length > 1 && <button onClick={() => haalWeg(kant, idx)} style={styles.removeBtn}><X size={16} /></button>}
        </div>
      ))}
      <button onClick={() => voegToe(kant)} style={styles.addArgBtn}><Plus size={16} style={{ marginRight: '4px', verticalAlign: 'middle' }} />Toevoegen</button>
    </div>
  );

  return (
    <div style={styles.card}>
      <div style={styles.argumentenHeader}>
        <GroepBadge groepje={groepje} discussion={discussion} />
        {tijdLooptNog && <Tijdklok seconden={klok.seconden} />}
      </div>
      <div style={styles.stepHeader}>
        <span style={styles.stepNumber}>4</span>
        <div><h2 style={styles.stepTitle}>Verzamel je argumenten</h2><p style={styles.stepSubtitle}>Wat heb je geleerd?</p></div>
      </div>
      <div style={styles.stellingCard}>
        <h3 style={styles.stellingLabel}>De stelling:</h3>
        <p style={styles.stellingTekst}>{session.statement?.titel}</p>
      </div>
      <div style={styles.argumentenGrid}>
        {kolom('voor', '✅ VOOR')}
        {kolom('tegen', '❌ TEGEN')}
      </div>

      <HulpVanSocrates hulp={hulp} />
      <GesprekMeelezen berichten={berichten} />

      <div style={styles.buttonRow}>
        <button onClick={onTerug} style={styles.btnSecondary}><ChevronLeft size={16} style={{ marginRight: '4px', verticalAlign: 'middle' }} />Terug naar chat</button>
        {tijdLooptNog ? (
          <button style={styles.btnDisabled} disabled>Opslaan (wacht op timer)</button>
        ) : (
          <button onClick={slaOp} style={styles.btn}>Opslaan →</button>
        )}
      </div>
    </div>
  );
}

const styles = {
  ...basis,
  argumentenHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', padding: '12px 16px', background: kleur.vlak, borderRadius: '8px' },
  argumentenGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', marginBottom: '24px' },
  argumentenColumn: { background: kleur.vlak, padding: '20px', borderRadius: '12px' },
  argumentenTitle: { color: 'black', marginBottom: '16px', fontSize: '16px' },
  argumentRow: { display: 'flex', gap: '8px', marginBottom: '12px' },
  argumentInput: { flex: 1, padding: '10px', border: `2px solid ${kleur.lijn}`, borderRadius: '6px', fontSize: '14px' },
  kritiekBtn: { width: '30px', height: '30px', flexShrink: 0, border: `2px solid ${kleur.accent}`, background: 'white', color: kleur.accentTekst, borderRadius: '50%', cursor: 'pointer', fontWeight: 'bold', font: 'inherit' },
  kritiekBtnUit: { width: '30px', height: '30px', flexShrink: 0, border: `2px solid ${kleur.lijn}`, background: kleur.vlakWarm, color: kleur.grijsLicht, borderRadius: '50%', cursor: 'default', fontWeight: 'bold', font: 'inherit' },
  removeBtn: { padding: '8px 12px', background: kleur.rood, color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer' },
  addArgBtn: { padding: '8px 16px', background: 'transparent', color: kleur.accent, border: `2px dashed ${kleur.accent}`, borderRadius: '6px', cursor: 'pointer', width: '100%' },
};
